import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Each notification kind has a sensible default time so a fresh enable
// lands somewhere useful without forcing the user through a time picker
// first. The daily-checkin default mirrors the legacy single-reminder
// time so existing users see no change.
const KIND_DEFAULTS = {
  daily_checkin: { hour: 9,  minute: 0  },
  meals:         { hour: 21, minute: 0  },
  water:         { hour: 20, minute: 0  },
  birthday:      { hour: 12, minute: 0  },   // birthday fires at noon; user time picker is hidden
  sleep:         { hour: 22, minute: 0  },   // sleep fires at plan-computed times; this is unused
}

const TABLE = 'notification_preferences'
const KINDS = Object.keys(KIND_DEFAULTS)

// Per-(user, kind) notification preferences. Returns a `prefs` map keyed
// by kind plus setEnabled / setTime helpers that upsert the row. The
// browser-push subscription itself is still managed by usePushSubscription
// — this hook only owns the schedule + on/off bits.
export function useNotificationPreferences() {
  const { user } = useAuth()
  const [prefs, setPrefs] = useState({})   // { kind: { enabled, reminder_hour, reminder_minute } }
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetchAll = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from(TABLE)
      .select('kind, enabled, reminder_hour, reminder_minute')
      .eq('user_id', user.id)
    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }
    const next = {}
    for (const row of data || []) next[row.kind] = row
    setPrefs(next)
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Get the existing row for a kind, or a sensible default if missing.
  const get = useCallback((kind) => {
    const row = prefs[kind]
    const defaults = KIND_DEFAULTS[kind] || { hour: 9, minute: 0 }
    return {
      enabled: !!row?.enabled,
      reminderHour: row?.reminder_hour ?? defaults.hour,
      reminderMinute: row?.reminder_minute ?? defaults.minute,
    }
  }, [prefs])

  // Upsert the (kind) row with the supplied changes — partial patch only.
  const upsert = useCallback(async (kind, patch) => {
    if (!user) return false
    if (!KINDS.includes(kind)) return false
    setBusy(true)
    setError(null)
    const existing = prefs[kind]
    const defaults = KIND_DEFAULTS[kind]
    const merged = {
      user_id: user.id,
      kind,
      enabled: patch.enabled ?? existing?.enabled ?? false,
      reminder_hour: patch.reminder_hour ?? existing?.reminder_hour ?? defaults.hour,
      reminder_minute: patch.reminder_minute ?? existing?.reminder_minute ?? defaults.minute,
      updated_at: new Date().toISOString(),
    }
    const { data, error: err } = await supabase
      .from(TABLE)
      .upsert(merged, { onConflict: 'user_id,kind' })
      .select('kind, enabled, reminder_hour, reminder_minute')
      .maybeSingle()
    setBusy(false)
    if (err) {
      setError(err.message)
      return false
    }
    setPrefs((p) => ({ ...p, [kind]: data || merged }))
    return true
  }, [user, prefs])

  const setEnabled = useCallback((kind, enabled) => upsert(kind, { enabled }), [upsert])

  const setTime = useCallback(
    (kind, hour, minute) => upsert(kind, { reminder_hour: hour, reminder_minute: minute }),
    [upsert],
  )

  return {
    prefs,         // raw row map, mainly for debugging
    get,           // returns { enabled, reminderHour, reminderMinute } per kind
    setEnabled,
    setTime,
    loading,
    busy,
    error,
    refetch: fetchAll,
    KINDS,
  }
}

export const NOTIFICATION_KINDS = KINDS
